import { type ELeadStage } from 'src/services/api/leads';

export enum EPlaybookTrigger {
  LeadUnresponsive = 'lead_unresponsive',
  TourStarted = 'tour_started',
  IncomingSms = 'incoming-sms',
  IncomingEmail = 'incoming-email',
  IncomingFacebookMessage = 'incoming-facebook-message',
  LeadIngested = 'lead_ingested',
  Manual = 'manual',
}

export const playbookTriggers = Object.values(EPlaybookTrigger);

type IPlaybookTriggerBase<
  T extends EPlaybookTrigger = EPlaybookTrigger,
  TParams = undefined,
> = {
  type: T;
  run_limit_per_lead: number;
  params?: TParams extends undefined ? never : TParams;
};

type ILeadUnresponsivePlaybookTriggerParams = {
  time_unit: 'days' | 'hours' | 'minutes';
  time_value: number;
};
type ILeadUnresponsivePlaybookTrigger = IPlaybookTriggerBase<
  EPlaybookTrigger.LeadUnresponsive,
  ILeadUnresponsivePlaybookTriggerParams
>;

type ITourStartedPlaybookTriggerParams = {
  time_offset_type: 'before' | 'after';
  time_offset_unit: 'days' | 'hours' | 'minutes';
  time_offset_value: number;
};
type ITourStartedPlaybookTrigger = IPlaybookTriggerBase<
  EPlaybookTrigger.TourStarted,
  ITourStartedPlaybookTriggerParams
>;

type IIncomingSmsPlaybookTrigger =
  IPlaybookTriggerBase<EPlaybookTrigger.IncomingSms>;

type IIncomingEmailPlaybookTrigger =
  IPlaybookTriggerBase<EPlaybookTrigger.IncomingEmail>;

type IIncomingFacebookMessagePlaybookTrigger =
  IPlaybookTriggerBase<EPlaybookTrigger.IncomingFacebookMessage>;

type ILeadIngestedPlaybookTrigger =
  IPlaybookTriggerBase<EPlaybookTrigger.LeadIngested>;

type IManualPlaybookTrigger = IPlaybookTriggerBase<EPlaybookTrigger.Manual>;

export type IPlaybookTrigger =
  | ILeadUnresponsivePlaybookTrigger
  | ITourStartedPlaybookTrigger
  | IIncomingSmsPlaybookTrigger
  | IIncomingEmailPlaybookTrigger
  | IIncomingFacebookMessagePlaybookTrigger
  | ILeadIngestedPlaybookTrigger
  | IManualPlaybookTrigger;

export enum EPlaybookStepTypeGroup {
  TriggerFilter = 'trigger-filter',
  Filter = 'filter',
  Action = 'action',
}

export const playbookStepTypeGroups = Object.values(EPlaybookStepTypeGroup);

export enum EPlaybookTriggerFilter {
  IsFirstMessageInThread = 'is-first-message-in-thread',
  SpecificProperty = 'specific-property',
  HasProperty = 'has-property',
  HasPropertyTourLink = 'has-property-tour-link',
  HasPropertyApplicationLink = 'has-property-application-link',
  LeadInStage = 'lead_in_stage',
  PropertyLabels = 'property_labels',
  HasLeadLabels = 'has_lead_labels',
  NoLeadLabels = 'no_lead_labels',
  NoShowingsBooked = 'no-showings-booked',
  LastMessageLanguage = 'last_message_language',
}

export const playbookTriggerFilters = Object.values(EPlaybookTriggerFilter);

export enum EPlaybookFilter {
  IsLastMessageFromLandlord = 'is-last-message-from-landlord',
  NoMessageHistory = 'no-message-history',
  HasProperty = 'has-property',
  SpecificProperty = 'specific-property',
  HasTourLink = 'has-property-tour-link',
  HasApplicationLink = 'has-property-application-link',
  LeadInStage = 'lead_in_stage',
  PropertyLabels = 'property_labels',
  HasLeadLabels = 'has_lead_labels',
  NoLeadLabels = 'no_lead_labels',
  NoShowingsBooked = 'no-showings-booked',
  LastMessageLanguage = 'last_message_language',
}

export const playbookFilters = Object.values(EPlaybookFilter);

export enum EPlaybookAction {
  SendMessage = 'send_message',
  SendSnippet = 'send_snippet',
  UpdateLabels = 'update_labels'
}

export const playbookActions = Object.values(EPlaybookAction);

type IHasLeadLabelsParams = {
  has_lead_label_ids: number[];
};

type INoLeadLabelsParams = {
  no_lead_label_ids: number[];
};

type IPlaybookTriggerFilterBase<
  T extends EPlaybookTriggerFilter,
  TParams = never,
> = {
  type_group: EPlaybookStepTypeGroup.TriggerFilter;
  type: T;
  params: TParams;
};

type IPlaybookTriggerFilterIsFirstMessageInThread =
  IPlaybookTriggerFilterBase<EPlaybookTriggerFilter.IsFirstMessageInThread>;

type IPlaybookTriggerFilterHasProperty =
  IPlaybookTriggerFilterBase<EPlaybookTriggerFilter.HasProperty>;

type IPlaybookTriggerFilterHasTourLink =
  IPlaybookTriggerFilterBase<EPlaybookTriggerFilter.HasPropertyTourLink>;

type IPlaybookTriggerFilterHasPropertyApplicationLink =
  IPlaybookTriggerFilterBase<EPlaybookTriggerFilter.HasPropertyApplicationLink>;

type IPlaybookTriggerFilterSpecificPropertyParams = { property_id: number };
type IPlaybookTriggerFilterSpecificProperty = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.SpecificProperty,
  IPlaybookTriggerFilterSpecificPropertyParams
>;

type IPlaybookTriggerFilterLeadStageParams = { lead_stages: ELeadStage[] };
type IPlaybookTriggerFilterLeadStage = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.LeadInStage,
  IPlaybookTriggerFilterLeadStageParams
>;

type IPlaybookTriggerFilterPropertyLabelParams = { property_label_ids: number[] };
type IPlaybookTriggerFilterPropertyLabel = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.PropertyLabels,
  IPlaybookTriggerFilterPropertyLabelParams
  >;

type IPlaybookTriggerFilterHasLeadLabels = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.HasLeadLabels,
  IHasLeadLabelsParams
>;

type IPlaybookTriggerFilterNoLeadLabels = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.NoLeadLabels,
  INoLeadLabelsParams
>;

type IPlaybookTriggerFilterNoShowingsBooked = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.NoShowingsBooked
>;

type IPlaybookTriggerFilterLastMessageLanguageParams = { message_languages: string[] };
type IPlaybookTriggerFilterLastMessageLanguage = IPlaybookTriggerFilterBase<
  EPlaybookTriggerFilter.LastMessageLanguage,
  IPlaybookTriggerFilterLastMessageLanguageParams
>;

type IPlaybookTriggerFilter =
  | IPlaybookTriggerFilterIsFirstMessageInThread
  | IPlaybookTriggerFilterHasProperty
  | IPlaybookTriggerFilterSpecificProperty
  | IPlaybookTriggerFilterHasTourLink
  | IPlaybookTriggerFilterHasPropertyApplicationLink
  | IPlaybookTriggerFilterLeadStage
  | IPlaybookTriggerFilterPropertyLabel
  | IPlaybookTriggerFilterHasLeadLabels
  | IPlaybookTriggerFilterNoLeadLabels
  | IPlaybookTriggerFilterNoShowingsBooked
  | IPlaybookTriggerFilterLastMessageLanguage;

type IPlaybookFilterBase<T extends EPlaybookFilter, TParams = never> = {
  type_group: EPlaybookStepTypeGroup.Filter;
  type: T;
  params: TParams;
};

type IPlaybookFilterIsLastMessageFromLandlord =
  IPlaybookFilterBase<EPlaybookFilter.IsLastMessageFromLandlord>;


type IPlaybookFilterNoMessageHistory =
  IPlaybookFilterBase<EPlaybookFilter.NoMessageHistory>;

type IPlaybookFilterSpecificPropertyParams = { property_id: number };
type IPlaybookFilterSpecificProperty = IPlaybookFilterBase<
  EPlaybookFilter.SpecificProperty,
  IPlaybookFilterSpecificPropertyParams
>;

type IPlaybookFilterHasProperty =
  IPlaybookFilterBase<EPlaybookFilter.HasProperty>;

type IPlaybookFilterHasTourLink =
  IPlaybookFilterBase<EPlaybookFilter.HasTourLink>;

type IPlaybookFilterHasApplicationLink =
  IPlaybookFilterBase<EPlaybookFilter.HasApplicationLink>;

type IPlaybookFilterNoShowingsBookedParams = { property_id: number };
type IPlaybookFilterNoShowingsBooked = IPlaybookFilterBase<
  EPlaybookFilter.NoShowingsBooked,
  IPlaybookFilterNoShowingsBookedParams
>;



type IPlaybookFilterLeadStageParams = { lead_stages: ELeadStage[] };
type IPlaybookFilterLeadStage = IPlaybookFilterBase<
  EPlaybookFilter.LeadInStage,
  IPlaybookFilterLeadStageParams
>;

type IPlaybookFilterPropertyLabelParams = { property_label_ids: number[] };
type IPlaybookFilterPropertyLabel = IPlaybookFilterBase<
  EPlaybookFilter.PropertyLabels,
  IPlaybookFilterPropertyLabelParams
>;

type IPlaybookFilterHasLeadLabels = IPlaybookFilterBase<
  EPlaybookFilter.HasLeadLabels,
  IHasLeadLabelsParams
>;

type IPlaybookFilterNoLeadLabels = IPlaybookFilterBase<
  EPlaybookFilter.NoLeadLabels,
  INoLeadLabelsParams
>;

type IPlaybookFilterLastMessageLanguageParams = { message_languages: string[] };
type IPlaybookFilterLastMessageLanguage = IPlaybookFilterBase<
  EPlaybookFilter.LastMessageLanguage,
  IPlaybookFilterLastMessageLanguageParams
>;

type IPlaybookFilter =
  | IPlaybookFilterIsLastMessageFromLandlord
  | IPlaybookFilterNoMessageHistory
  | IPlaybookFilterHasProperty
  | IPlaybookFilterSpecificProperty
  | IPlaybookFilterHasTourLink
  | IPlaybookFilterHasApplicationLink
  | IPlaybookFilterNoShowingsBooked
  | IPlaybookFilterLeadStage
  | IPlaybookFilterPropertyLabel
  | IPlaybookFilterHasLeadLabels
  | IPlaybookFilterNoLeadLabels
  | IPlaybookFilterLastMessageLanguage;

type IPlaybookActionBase<T extends EPlaybookAction, TParams = never> = {
  type_group: EPlaybookStepTypeGroup.Action;
  type: T;
  params: TParams;
};

type IPlaybookActionSendMessageParams = {
  channel: string;
  message: string;
};
type IPlaybookActionSendMessage = IPlaybookActionBase<
  EPlaybookAction.SendMessage,
  IPlaybookActionSendMessageParams
>;

type IPlaybookActionSendSnippetParams = {
  message: string;
};
type IPlaybookActionSendSnippet = IPlaybookActionBase<
  EPlaybookAction.SendSnippet,
  IPlaybookActionSendSnippetParams
>;

type IPlaybookActionUpdateLabelsParams = {
  add_label_ids: number[];
  delete_label_ids: number[];
  delete_existing_labels: boolean;
}

type IPlaybookActionUpdateLabel = IPlaybookActionBase<
  EPlaybookAction.UpdateLabels,
  IPlaybookActionUpdateLabelsParams
>;


type IPlaybookAction = IPlaybookActionSendMessage | IPlaybookActionSendSnippet | IPlaybookActionUpdateLabel;

type IPlaybookStepBase<
  T = IPlaybookTriggerFilter | IPlaybookFilter | IPlaybookAction,
> = T & {
  children: IPlaybookNode[] | null;
};

export type IPlaybookTriggerFilterStep =
  IPlaybookStepBase<IPlaybookTriggerFilter>;
export type IPlaybookFilterStep = IPlaybookStepBase<IPlaybookFilter>;
export type IPlaybookActionStep = IPlaybookStepBase<IPlaybookAction>;

export type IPlaybookStep =
  | IPlaybookTriggerFilterStep
  | IPlaybookFilterStep
  | IPlaybookActionStep;

export type IPlaybookNode = {
  // condition: never;
  step: IPlaybookStep;
};

/**
 * Lead filter params. Sent as-is in the playbook PATCH payload.
 */
export interface IPlaybookLeadFilterParams {
  property_id?: number;

  // time-based playbooks parameters
  time_based_context?: 'before' | 'after';
  time_based_minutes?: number;

  // last inbound message language filter (BCP 47 / ISO 639-1 codes)
  message_languages?: string[];

  // lead stage filter
  lead_stages?: ELeadStage[];

  // lead label filters - each reads its own key so both can be used on one playbook
  has_lead_label_ids?: number[];
  no_lead_label_ids?: number[];
}

export enum PlaybookRunStatus {
  // playbook has been queued for a run but has not been picked up by a worker
  QUEUED = 'queued',
  // playbook has been picked up by a worker and is being executed
  RUNNING = 'running',
  // playbook has completed running and did not suffer catastrophic errors
  COMPLETED = 'completed',
  // playbook failed catastrophically and was not able to complete
  FAILED = 'failed',
}

export interface IPlaybookRun {
  id: number;
  num_rows_affected?: number | null;
  status: PlaybookRunStatus;
  // timezone is always present inside the string
  start_time: string | null;
  stop_time: string | null;
}

/**
 * Currently supported lead filters.
 * Should match exactly with AutomationLeadFilter in
 * reffie_common_utils_py/services/automations/types.py
 */
export enum LeadFilter {
  ValidPhoneNumber = 'valid-phone-number',
  ValidEmail = 'valid-email',
  NoMessageHistory = 'no-message-history',
  IsLastEmailFromStreetEasy = 'is-last-email-from-street-easy',
  IsLastMessageFromLandlord = 'is-last-message-from-landlord',
  IsLastMessageFromLead = 'is-last-mesage-from-lead',
  IsLastMessageFromListingSite = 'is-last-message-from-listing-site',
  IsFirstMessageInThread = 'is-first-message-in-thread',
  IsNewLead = 'is-new-lead',
  IsLeadSourceListingSite = 'is-lead-source-listing-site',
  IsIngestionSourceZillow = 'ingestion-source-zillow',
  LastMessageSentOver2DaysAgo = 'last-message-sent-over-2-days-ago',
  NoLandlordResponse = 'no-landlord-response',
  NoRentalApplicationInvite = 'no-rental-application-invite',
  IsLastMessageApplicationRequest = 'is-last-message-application-request',
  HasProperty = 'has-property',
  SpecificProperty = 'specific-property',
  NoShowingsBooked = 'no-showings-booked',
  HasPropertyApplicationLink = 'has-property-application-link',
  HasPropertyTourLink = 'has-property-tour-link',
  LastMessageFromLandlordWasSpecificTimeAgo = 'last-message-from-landlord-was-specific-time-ago',
  ShowingScheduled = 'showing-scheduled',
  LastMessageLanguage = 'last_message_language',
}

/**
 * A playbook as returned by GET /api/landlord/playbooks.
 * Mirrors PlaybookResponseSchema in reffie/services/playbooks/types.py.
 */
export interface IPlaybookV2 {
  // numeric playbook id; null for playbooks created before the dual-write backfill (REF-7933)
  id: number | null;
  uuid: string;
  landlord_user_id: number;
  creator_id?: number | null;
  title: string;
  description: string;
  trigger: IPlaybookTrigger;
  is_enabled: boolean;
  created_at: string | null;
  modified_at: string | null;
  first_invoked_at: string | null;
  last_invoked_at: string | null;
  num_invocations: number | null;
  num_rows_affected: number | null;
  priority: number | null;
  latest_run: IPlaybookRun | null;
  first_step: IPlaybookStep;
}
